import { getItemTypes } from '../../config/itemTypes';
import { getSetTypes } from '../../config/setTypes';

/**
 * Manufacturing Item Type
 * @deprecated This type should be replaced with string to allow dynamic item types from item_masters table.
 * For now, keeping as string to support dynamic item types.
 */
export type ManufacturingItemType = string;

export type ItemStatus = "pending" | "in_progress" | "completed" | "dispatched";

export type StageStatus =
  | "not_started"
  | "in_progress"
  | "completed"
  | "on_hold";

// New status types with integer values
export type OrderStatusValue = 0 | 1 | 2 | 3; // pending, in_progress, completed, dispatched
export type ProductStatusValue = 0 | 1 | 2 | 3; // pending, in_progress, completed, dispatched
export type ItemStatusValue = 0 | 1 | 2; // pending, in_progress, completed
export type StageStatusValue = 0 | 1 | 2; // pending, in_progress, completed

// Stage interface for workflow tracking
export interface Stage {
  id: string;
  name: string;
  order: number;
  status: StageStatusValue;
  completed: boolean;
  startedAt?: Date;
  completedAt?: Date;
  assignedTo?: string;
  notes?: string;
}

// Manufacturing Workflow Stages for each item type
export const MANUFACTURING_STAGES: Readonly<
  Record<ManufacturingItemType, readonly string[]>
> = {
  Frame: ["Fabrication", "Coating", "Dispatch"],
  Panel: [
    "Cutting",
    "Assembly",
    "Hinges",
    "Member",
    "Bar",
    "Full Welding",
    "Grinding",
    "Leveling",
    "Finishing",
    "Khacha",
    "Coating",
    "Accessories",
    "Glass Fitting",
    "Dispatch",
  ],
  "Panel Window": ["Fabrication", "Coating", "Fitting in Panel"],
  "Net Fixing": ["Frame Cut", "Weld", "Net Fixing"],
};

export interface WorkflowStage {
  id: string;
  stageName: string; // Updated from skillName to stageName
  order: number;
  itemType: ManufacturingItemType;
  description?: string;
  estimatedDuration?: number; // in hours
  requiredRoles: string[];
  stageCode: string; // Updated from skillCode to stageCode - Human-readable identifier like "CUT001", "ASS001"
  dependencies: string[]; // Array of stage IDs this stage depends on
  parallelStages: string[]; // Array of stage IDs that can run in parallel
  estimatedDurationHours: number;
  requiredSkills: string[];
  requiresQrScan: boolean;
  requiresPhoto: boolean;
  requiresSupervisorApproval: boolean;
  stageCategory?: string; // e.g., "fabrication", "finishing", "dispatch", or custom
  customCategory?: string; // For user-defined categories
  isMilestone: boolean; // Marks important completion points
  metadata: Record<string, any>;
}

export interface ItemLog {
  id: string;
  itemId: string;
  stage: string;
  previousStage?: string;
  userId: string;
  userName: string;
  timestamp: Date;
  notes?: string;
  photoUrl?: string;
  location?: string;
  status: StageStatus;
}

export interface Panel {
  id: string;
  number: number;
  height: string;
  width: string;
  member_required: boolean;
  bar_required: boolean;
  glass_required: boolean;
}

export interface Net {
  id: string;
  number: number;
  height: string;
  width: string;
}

export type ItemType = "Door" | "Window" | "Ventilator" | "Net Fix";

export type SubItemType = "Leaf" | "Panel Window" | "Net Fixing" | "Future";

export interface SubItem {
  id: string;
  number: number;
  type: string;
  width: string;
  height: string;
}

export interface WorkflowTemplate {
  id: string;
  name: string;
  itemType: ManufacturingItemType;
  stages: WorkflowStage[];
  status: boolean;
  createdAt: Date;
  updatedAt: Date;

  // New fields for enhanced workflow management
  workflowCode: string; // Human-readable workflow identifier like "WF-PANEL-001"
  stageSequence: string[]; // Ordered array of stage IDs with dependencies
  parallelGroups: string[][]; // Groups of stages that can run in parallel
  totalStages: number;
  requiresApproval: boolean;
  approvalStages: string[]; // Stages that require approval
  estimatedTotalHours: number;
  description?: string;
}

// Dashboard Analytics Types
export interface DashboardStats {
  totalOrders: number;
  totalItems: number;
  completedItems: number;
  pendingItems: number;
  inProgressItems: number;
  todayProduction: number;
  averageCompletionTime: number; // in hours
}

export interface StageProgress {
  stageName: string;
  itemType: ManufacturingItemType;
  totalItems: number;
  completedItems: number;
  inProgressItems: number;
  averageTimeSpent: number; // in hours
}

// API Response Types
export interface ItemUpdateRequest {
  status?: ItemStatus;
  currentStage?: string;
  assignedTo?: string;
  estimatedCompletion?: Date;
  completedAt?: Date;
  notes?: string;
}

// Enhanced workflow progress tracking
export interface WorkflowProgress {
  totalStages: number;
  completedStages: number;
  inProgressStages: number;
  progressPercentage: number;
  currentStage: CurrentStage | null;
  nextStages: AvailableStage[];
  blockedStages: BlockedStage[];
  stageLogs: StageLog[];
}

export interface CurrentStage {
  name: string;
  code: string;
  order: number;
  status: string;
  startedAt: string;
  assignedTo: string;
}

export interface AvailableStage {
  id: string;
  name: string;
  code: string;
  order: number;
  description: string;
  estimatedDurationHours: number;
  requiredSkills: string[];
  requiresQrScan: boolean;
  requiresPhoto: boolean;
  requiresSupervisorApproval: boolean;
  stageCategory: string;
  isMilestone: boolean;
  canStart: boolean;
  reason: string;
}

export interface BlockedStage {
  id: string;
  name: string;
  code: string;
  order: number;
  description: string;
  estimatedDurationHours: number;
  requiredSkills: string[];
  requiresQrScan: boolean;
  requiresPhoto: boolean;
  requiresSupervisorApproval: boolean;
  stageCategory: string;
  isMilestone: boolean;
  canStart: boolean;
  reason: string;
  missingDependencies: string[];
}

export interface StageLog {
  name: string;
  code: string;
  order: number;
  status: string;
  startedAt: string | null;
  completedAt: string | null;
  assignedTo: string | null;
  completedBy: string | null;
  notes: string | null;
  photos: string[];
}

// Enhanced QR scan result with workflow information
export interface QRScanResult {
  item: Item;
  order: Order;
  product: Product;
  allowedNextStages: string[];

  // New workflow information
  workflow: WorkflowProgress;
}

export type ProductType = "door" | "window" | "ventilator" | "net fix";

export interface SubItemForm {
  id: string;
  type: string;
  specifications: {
    number: number;
    width: string;
    height: string;
    material?: string;
    color?: string;
    finish?: string;
  };
  quantity: number;
}

export interface ProductForm {
  id: string;
  type: ProductType;
  evaOrder: string;
  label: string;
  location: string;
  notes: string;
  specifications?: {
    number: number;
    member_required?: boolean;
    bar_required?: boolean;
    glass_required?: boolean;
    width: string;
    height: string;
    material?: string;
    color?: string;
    finish?: string;
  };
  netType?: "Fixed" | "Sliding" | "Openable";
  netCount: number;
  // New grouped item structure
  frame?: SubItemForm[];
  panel?: SubItemForm[];
  panelWindow?: SubItemForm[];
  net?: SubItemForm[];
  openableType?: SubItemForm[];
  // Legacy support
  items?: SubItemForm[];
}

export interface WorkflowLog {
  id: string;
  stage: string;
  previous_stage: string | null;
  user_id: string;
  user_name: string;
  status: string;
  notes: string;
  data_changes: Record<string, any>;
  photo_url: string | null;
  duration_hours: number | null;
  quality_check: Record<string, any>;
  metadata: Record<string, any>;
  timestamp: string;
  created_at: string;
  updated_at: string;
}

export interface FrameItem {
  id: string;
  order_id: string;
  product_id: string;
  qr_code: string;
  qr_code_image_url: string | null;
  type: string;
  current_stage: string;
  current_stage_order: number;
  status: "pending" | "in_progress" | "completed" | "dispatched";
  specifications: Record<string, any>;
  supervisor_id: string | null;
  started_at: string | null;
  completed_at: string | null;
  estimated_hours: number | null;
  actual_hours: number | null;
  workflow_data: Record<string, any>;
  metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
  quantity: number;
  deleted: boolean;
  deleted_at: string | null;
  deleted_by: string | null;
  logs: WorkflowLog[];
}

// New Product interface with item type arrays
export interface ProductWithItemTypes {
  id: string;
  order_id: string;
  name: string;
  type: string;
  description: string;
  total_items: number;
  completed_items: number;
  unit_price: number | null;
  quantity: number;
  specifications: Record<string, any>;
  metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
  label: string | null;
  location: string | null;
  notes: string | null;
  eva_order: string | null;
  deleted: boolean;
  deleted_at: string | null;
  deleted_by: string | null;

  // New item type arrays
  frame_items: FrameItem[];
  door_items: FrameItem[];
  net_items: FrameItem[];
  openable_type_items: FrameItem[];
  panel_items: FrameItem[];
  panel_window_items: FrameItem[];
}

// New Order interface for the updated structure
export interface OrderWithItemTypes {
  id: string;
  order_number: string;
  customer_id: string;
  customer: Customer;
  site_id?: string;
  site?: any;
  total_amount: number;
  due_date: string;
  notes: string;
  priority: "low" | "medium" | "high" | "urgent";
  status:
  | "draft"
  | "pending"
  | "in_progress"
  | "completed"
  | "dispatched"
  | "cancelled";
  payment_status: "pending" | "partial" | "paid" | "overdue" | "refunded";
  progress_percentage: number;
  total_items: number;
  completed_items: number;
  products: ProductWithItemTypes[];
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

// Form interfaces for creating/updating orders
export interface CreateOrderFormData {
  customer_id: string;
  site_id?: string;
  total_amount: number;
  due_date: string;
  notes: string;
  priority: "low" | "medium" | "high" | "urgent";
  payment_status: "pending" | "partial" | "paid";
  products: CreateProductFormData[];
}

// Dynamic item type configuration
export interface ItemTypeConfig {
  key: string;
  label: string;
  icon: string;
  color: string;
  workflowStages: string[];
}

/**
 * Get item type configurations dynamically based on item types from the database.
 * This function fetches item types from the item_masters table and returns
 * configurations. The icon, color, and workflowStages are mapped based on
 * the item type name.
 * 
 * @returns Promise resolving to array of item type configurations
 */
export async function getDefaultItemTypes(): Promise<ItemTypeConfig[]> {
  const itemTypes = await getItemTypes();

  // Map item type names to their configurations
  // Note: This mapping should ideally come from the database as well
  // For now, we use a mapping based on item type names
  const configMap: Record<string, Partial<ItemTypeConfig>> = {
    'Frame': {
      key: "frame",
      icon: "Engineering",
      color: "#1976d2",
      workflowStages: ["Fabrication", "Coating", "Dispatch"],
    },
    'frame': {
      key: "frame",
      icon: "Engineering",
      color: "#1976d2",
      workflowStages: ["Fabrication", "Coating", "Dispatch"],
    },
    'Panel': {
      key: "panel",
      icon: "GridOn",
      color: "#388e3c",
      workflowStages: [
        "Cutting",
        "Assembly",
        "Hinges",
        "Member",
        "Bar",
        "Full Welding",
        "Grinding",
        "Leveling",
        "Finishing",
        "Khacha",
        "Coating",
        "Accessories",
        "Glass Fitting",
        "Dispatch",
      ],
    },
    'panel': {
      key: "panel",
      icon: "GridOn",
      color: "#388e3c",
      workflowStages: [
        "Cutting",
        "Assembly",
        "Hinges",
        "Member",
        "Bar",
        "Full Welding",
        "Grinding",
        "Leveling",
        "Finishing",
        "Khacha",
        "Coating",
        "Accessories",
        "Glass Fitting",
        "Dispatch",
      ],
    },
    'Panel Window': {
      key: "panelWindow",
      icon: "Window",
      color: "#f57c00",
      workflowStages: ["Fabrication", "Coating", "Fitting in Panel"],
    },
    'panelWindow': {
      key: "panelWindow",
      icon: "Window",
      color: "#f57c00",
      workflowStages: ["Fabrication", "Coating", "Fitting in Panel"],
    },
    'panel window': {
      key: "panelWindow",
      icon: "Window",
      color: "#f57c00",
      workflowStages: ["Fabrication", "Coating", "Fitting in Panel"],
    },
    'Net Fixing': {
      key: "net",
      icon: "GridOn",
      color: "#7b1fa2",
      workflowStages: ["Frame Cut", "Weld", "Net Fixing"],
    },
    'net fixing': {
      key: "net",
      icon: "GridOn",
      color: "#7b1fa2",
      workflowStages: ["Frame Cut", "Weld", "Net Fixing"],
    },
    'net': {
      key: "net",
      icon: "GridOn",
      color: "#7b1fa2",
      workflowStages: ["Frame Cut", "Weld", "Net Fixing"],
    },
    'Openable Type': {
      key: "openableType",
      icon: "OpenInNew",
      color: "#d32f2f",
      workflowStages: [
        "Design",
        "Fabrication",
        "Assembly",
        "Coating",
        "Dispatch",
      ],
    },
    'openable type': {
      key: "openableType",
      icon: "OpenInNew",
      color: "#d32f2f",
      workflowStages: [
        "Design",
        "Fabrication",
        "Assembly",
        "Coating",
        "Dispatch",
      ],
    },
    'openableType': {
      key: "openableType",
      icon: "OpenInNew",
      color: "#d32f2f",
      workflowStages: [
        "Design",
        "Fabrication",
        "Assembly",
        "Coating",
        "Dispatch",
      ],
    },
  };

  return itemTypes.map(itemType => {
    const config = configMap[itemType.value] || configMap[itemType.label] || {
      key: itemType.value.toLowerCase().replace(/\s+/g, ''),
      icon: "Category",
      color: "#757575",
      workflowStages: [],
    };

    return {
      key: config.key || itemType.value.toLowerCase().replace(/\s+/g, ''),
      label: itemType.label,
      icon: config.icon || "Category",
      color: config.color || "#757575",
      workflowStages: config.workflowStages || [],
    };
  });
}

// Product type configuration with customizable item types
export interface ProductTypeConfig {
  type: string;
  name: string;
  description: string;
  itemTypes: string[]; // Array of item type keys that this product type supports
  defaultSpecifications: Record<string, any>;
}

/**
 * Get product type configurations dynamically based on set types from the database.
 * This function fetches set types from the product_masters table and returns
 * configurations. The itemTypes and defaultSpecifications are mapped based on
 * the set type name.
 * 
 * @returns Promise resolving to array of product type configurations
 */
export async function getProductTypeConfigs(): Promise<ProductTypeConfig[]> {
  const setTypes = await getSetTypes();

  // Map set type names to their configurations
  // Note: This mapping should ideally come from the database as well
  // For now, we use a mapping based on set type names
  const configMap: Record<string, Partial<ProductTypeConfig>> = {
    'door': {
      name: "Door",
      description: "Manufacturing door with frame and panel",
      itemTypes: ["frame", "panel", "panelWindow"],
      defaultSpecifications: {
        includeFrame: true,
        panelQuantity: 1,
        panelWindowQuantity: 0,
      },
    },
    'Door': {
      name: "Door",
      description: "Manufacturing door with frame and panel",
      itemTypes: ["frame", "panel", "panelWindow"],
      defaultSpecifications: {
        includeFrame: true,
        panelQuantity: 1,
        panelWindowQuantity: 0,
      },
    },
    'window': {
      name: "Window",
      description: "Manufacturing window with frame and panel",
      itemTypes: ["frame", "panel", "panelWindow"],
      defaultSpecifications: {
        includeFrame: true,
        panelQuantity: 1,
        panelWindowQuantity: 1,
      },
    },
    'Window': {
      name: "Window",
      description: "Manufacturing window with frame and panel",
      itemTypes: ["frame", "panel", "panelWindow"],
      defaultSpecifications: {
        includeFrame: true,
        panelQuantity: 1,
        panelWindowQuantity: 1,
      },
    },
    'ventilator': {
      name: "Ventilator",
      description: "Manufacturing ventilator with frame and net",
      itemTypes: ["frame", "net"],
      defaultSpecifications: {
        includeFrame: true,
        netQuantity: 1,
      },
    },
    'Ventilator': {
      name: "Ventilator",
      description: "Manufacturing ventilator with frame and net",
      itemTypes: ["frame", "net"],
      defaultSpecifications: {
        includeFrame: true,
        netQuantity: 1,
      },
    },
    'netFix': {
      name: "Net Fix",
      description: "Net fixing installation",
      itemTypes: ["net"],
      defaultSpecifications: {
        netQuantity: 1,
      },
    },
    'Net Fix': {
      name: "Net Fix",
      description: "Net fixing installation",
      itemTypes: ["net"],
      defaultSpecifications: {
        netQuantity: 1,
      },
    },
    'Net Fixing': {
      name: "Net Fix",
      description: "Net fixing installation",
      itemTypes: ["net"],
      defaultSpecifications: {
        netQuantity: 1,
      },
    },
  };

  return setTypes.map(setType => {
    const config = configMap[setType.value] || configMap[setType.label] || {
      name: setType.label,
      description: `${setType.label} configuration`,
      itemTypes: [],
      defaultSpecifications: {},
    };

    return {
      type: setType.value,
      name: config.name || setType.label,
      description: config.description || `${setType.label} configuration`,
      itemTypes: config.itemTypes || [],
      defaultSpecifications: config.defaultSpecifications || {},
    };
  });
}

// PRODUCT_TYPE_CONFIGS has been replaced with getProductTypeConfigs() function
// All product type values must be dynamically fetched from the product_masters table.

export interface CreateProductFormData {
  id: string;
  name: string;
  type: string;
  description: string;
  unit_price?: number;
  quantity: number;
  specifications: Record<string, any>;
  label?: string;
  location?: string;
  notes?: string;
  eva_order?: string;

  // New dynamic item types structure
  itemTypes: Record<string, CreateItemFormData[]>;
}

export interface CreateItemFormData {
  type: string;
  quantity: number;
  specifications: {
    width: string;
    height: string;
    material: string;
    finish: string;
    [key: string]: any;
  };
  estimated_hours: number;
  // Product information for tracking
  productName?: string;
  productType?: string;
}

// Workflow stage categories for better organization
export const WORKFLOW_STAGE_CATEGORIES = [
  "fabrication",
  "finishing",
  "assembly",
  "quality",
  "dispatch",
  "custom",
] as const;

export type WorkflowStageCategory = (typeof WORKFLOW_STAGE_CATEGORIES)[number];

// Default workflow templates with dependencies
export const DEFAULT_WORKFLOW_TEMPLATES: Omit<
  WorkflowTemplate,
  "id" | "createdAt" | "updatedAt"
>[] = [
    {
      name: "Panel Manufacturing Workflow",
      itemType: "Panel",
      workflowCode: "WF-PANEL-001",
      stageSequence: [],
      parallelGroups: [],
      totalStages: 14,
      requiresApproval: false,
      approvalStages: [],
      estimatedTotalHours: 20,
      description:
        "Complete panel manufacturing workflow from cutting to dispatch",
      status: true,
      stages: [
        {
          id: "cutting-001",
          name: "Cutting",
          code: "CUT001",
          order: 1,
          itemType: "Panel",
          description: "Cut materials to required dimensions",
          estimatedDuration: 2,
          estimatedDurationHours: 2,
          requiredRoles: ["cutter", "operator"],
          requiredSkills: ["cutting", "measurement"],
          requiresQrScan: true,
          requiresPhoto: false,
          requiresSupervisorApproval: false,
          stageCategory: "fabrication",
          isMilestone: false,
          dependencies: [],
          parallelStages: [],
          metadata: {},
        },
        {
          id: "assembly-002",
          name: "Assembly",
          code: "ASS001",
          order: 2,
          itemType: "Panel",
          description: "Assemble cut pieces",
          estimatedDuration: 3,
          estimatedDurationHours: 3,
          requiredRoles: ["assembler", "operator"],
          requiredSkills: ["assembly", "welding"],
          requiresQrScan: true,
          requiresPhoto: false,
          requiresSupervisorApproval: false,
          stageCategory: "fabrication",
          isMilestone: false,
          dependencies: ["cutting-001"],
          parallelStages: [],
          metadata: {},
        },
        {
          id: "hinges-003",
          name: "Hinges",
          code: "HIN001",
          order: 3,
          itemType: "Panel",
          description: "Install hinges",
          estimatedDuration: 1,
          estimatedDurationHours: 1,
          requiredRoles: ["installer", "operator"],
          requiredSkills: ["hinge_installation"],
          requiresQrScan: true,
          requiresPhoto: false,
          requiresSupervisorApproval: false,
          stageCategory: "fabrication",
          isMilestone: false,
          dependencies: ["assembly-002"],
          parallelStages: [],
          metadata: {},
        },
        {
          id: "member-004",
          name: "Member",
          code: "MEM001",
          order: 4,
          itemType: "Panel",
          description: "Install structural members",
          estimatedDuration: 1,
          estimatedDurationHours: 1,
          requiredRoles: ["installer", "operator"],
          requiredSkills: ["structural_installation"],
          requiresQrScan: true,
          requiresPhoto: false,
          requiresSupervisorApproval: false,
          stageCategory: "fabrication",
          isMilestone: false,
          dependencies: ["hinges-003"],
          parallelStages: [],
          metadata: {},
        },
        {
          id: "bar-005",
          name: "Bar",
          code: "BAR001",
          order: 5,
          itemType: "Panel",
          description: "Install support bars",
          estimatedDuration: 1,
          estimatedDurationHours: 1,
          requiredRoles: ["installer", "operator"],
          requiredSkills: ["bar_installation"],
          requiresQrScan: true,
          requiresPhoto: false,
          requiresSupervisorApproval: false,
          stageCategory: "fabrication",
          isMilestone: false,
          dependencies: ["member-004"],
          parallelStages: [],
          metadata: {},
        },
        {
          id: "welding-006",
          name: "Full Welding",
          code: "WEL001",
          order: 6,
          itemType: "Panel",
          description: "Complete welding of all joints",
          estimatedDuration: 2,
          estimatedDurationHours: 2,
          requiredRoles: ["welder", "operator"],
          requiredSkills: ["welding", "quality_check"],
          requiresQrScan: true,
          requiresPhoto: true,
          requiresSupervisorApproval: true,
          stageCategory: "fabrication",
          isMilestone: true,
          dependencies: ["bar-005"],
          parallelStages: [],
          metadata: {},
        },
        {
          id: "grinding-007",
          name: "Grinding",
          code: "GRI001",
          order: 7,
          itemType: "Panel",
          description: "Grind welded joints smooth",
          estimatedDuration: 1,
          estimatedDurationHours: 1,
          requiredRoles: ["finisher", "operator"],
          requiredSkills: ["grinding", "finishing"],
          requiresQrScan: true,
          requiresPhoto: false,
          requiresSupervisorApproval: false,
          stageCategory: "finishing",
          isMilestone: false,
          dependencies: ["welding-006"],
          parallelStages: [],
          metadata: {},
        },
        {
          id: "leveling-008",
          name: "Leveling",
          code: "LEV001",
          order: 8,
          itemType: "Panel",
          description: "Level and align panel",
          estimatedDuration: 1,
          estimatedDurationHours: 1,
          requiredRoles: ["finisher", "operator"],
          requiredSkills: ["leveling", "alignment"],
          requiresQrScan: true,
          requiresPhoto: false,
          requiresSupervisorApproval: false,
          stageCategory: "finishing",
          isMilestone: false,
          dependencies: ["grinding-007"],
          parallelStages: [],
          metadata: {},
        },
        {
          id: "finishing-009",
          name: "Finishing",
          code: "FIN001",
          order: 9,
          itemType: "Panel",
          description: "Final finishing touches",
          estimatedDuration: 2,
          estimatedDurationHours: 2,
          requiredRoles: ["finisher", "operator"],
          requiredSkills: ["finishing", "quality_check"],
          requiresQrScan: true,
          requiresPhoto: true,
          requiresSupervisorApproval: true,
          stageCategory: "finishing",
          isMilestone: true,
          dependencies: ["leveling-008"],
          parallelStages: [],
          metadata: {},
        },
        {
          id: "khacha-010",
          name: "Khacha",
          code: "KHA001",
          order: 10,
          itemType: "Panel",
          description: "Apply khacha finish",
          estimatedDuration: 1,
          estimatedDurationHours: 1,
          requiredRoles: ["finisher", "operator"],
          requiredSkills: ["khacha_application"],
          requiresQrScan: true,
          requiresPhoto: false,
          requiresSupervisorApproval: false,
          stageCategory: "finishing",
          isMilestone: false,
          dependencies: ["finishing-009"],
          parallelStages: [],
          metadata: {},
        },
        {
          id: "coating-011",
          name: "Coating",
          code: "COA001",
          order: 11,
          itemType: "Panel",
          description: "Apply protective coating",
          estimatedDuration: 3,
          estimatedDurationHours: 3,
          requiredRoles: ["coater", "operator"],
          requiredSkills: ["coating", "quality_check"],
          requiresQrScan: true,
          requiresPhoto: true,
          requiresSupervisorApproval: true,
          stageCategory: "finishing",
          isMilestone: true,
          dependencies: ["khacha-010"],
          parallelStages: [],
          metadata: {},
        },
        {
          id: "accessories-012",
          name: "Accessories",
          code: "ACC001",
          order: 12,
          itemType: "Panel",
          description: "Install accessories and hardware",
          estimatedDuration: 1,
          estimatedDurationHours: 1,
          requiredRoles: ["installer", "operator"],
          requiredSkills: ["accessory_installation"],
          requiresQrScan: true,
          requiresPhoto: false,
          requiresSupervisorApproval: false,
          stageCategory: "assembly",
          isMilestone: false,
          dependencies: ["coating-011"],
          parallelStages: [],
          metadata: {},
        },
        {
          id: "glass-013",
          name: "Glass Fitting",
          code: "GLA001",
          order: 13,
          itemType: "Panel",
          description: "Fit glass panels",
          estimatedDuration: 2,
          estimatedDurationHours: 2,
          requiredRoles: ["installer", "operator"],
          requiredSkills: ["glass_fitting", "quality_check"],
          requiresQrScan: true,
          requiresPhoto: true,
          requiresSupervisorApproval: false,
          stageCategory: "assembly",
          isMilestone: false,
          dependencies: ["accessories-012"],
          parallelStages: [],
          metadata: {},
        },
        {
          id: "dispatch-014",
          name: "Dispatch",
          code: "DIS001",
          order: 14,
          itemType: "Panel",
          description: "Prepare for dispatch",
          estimatedDuration: 1,
          estimatedDurationHours: 1,
          requiredRoles: ["dispatcher", "operator"],
          requiredSkills: ["packaging", "dispatch"],
          requiresQrScan: true,
          requiresPhoto: true,
          requiresSupervisorApproval: true,
          stageCategory: "dispatch",
          isMilestone: true,
          dependencies: ["glass-013"],
          parallelStages: [],
          metadata: {},
        },
      ],
    },
  ];

// Enhanced tracking code interfaces
export interface TrackingCode {
  customer: string; // CUST-ABC-001
  order: string; // ORD-2024-001
  product: string; // PRD-DOO-001
  item: string; // ITM-PNL-ORD-2024-001-001
  site?: string; // SITE-MAIN-001
}

export interface TrackingInfo {
  fullPath: string; // CUST-ABC-001 → ORD-2024-001 → PRD-DOO-001 → ITM-PNL-ORD-2024-001-001
  humanReadable: string; // ABC Company (ABC) → Order ORD-2024-001 → Door → Panel 001
}

export interface ItemTrackingData {
  item: {
    id: string;
    trackingCode: string;
    type: string;
    currentStage: string;
    status: string;
    qrCode: string;
    specifications: Record<string, any>;
  };
  product: {
    id: string;
    trackingCode: string;
    type: string;
    name: string;
  };
  order: {
    id: string;
    trackingCode: string;
    dueDate: string;
  };
  customer: {
    id: string;
    trackingCode: string;
    name: string;
    code: string;
  };
  tracking: TrackingInfo;
}

// Enhanced interfaces with tracking codes
export interface Customer {
  id: string;
  customer_tracking_code: string; // CUST-ABC-001
  name: string;
  code: string; // ABC, XYZ, etc.
  contactPerson: string;
  phone: string;
  email: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  status?: boolean;
  industry?: string;
  customerType?: string; // regular, premium, vip
  creditLimit?: number;
  metadata?: Record<string, any>;
}

// Statistics interfaces for comprehensive order tracking
export interface ProductStatistics {
  totalProducts: number;
  completedProducts: number;
  pendingProducts: number;
  inProgressProducts: number;
  dispatchedProducts: number;
  progressPercentage: number;
}

export interface ItemStatistics {
  totalItems: number;
  completedItems: number;
  pendingItems: number;
  inProgressItems: number;
  progressPercentage: number;
}

export interface OrderProgress {
  overallProgressPercentage: number;
  isCompleted: boolean;
  isDispatched: boolean;
  isInProgress: boolean;
  isPending: boolean;
}

export interface Order {
  id: string;
  order_tracking_code: string; // ORD-2024-001
  orderNumber?: string; // Legacy order number
  orderNumberNew?: string; // New format: 25080001
  customer_id: string;
  customer: Customer;
  site_id?: string;
  site_tracking_code?: string; // SITE-MAIN-001
  site?: {
    id: string;
    name: string;
    address?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    contactPerson?: string;
    contactPhone?: string;
    status?: string;
    notes?: string;
  };
  totalAmount: string | number;
  dueDate: string;
  notes: string;
  priority: "low" | "medium" | "high" | "urgent";
  status:
  | "draft"
  | "pending"
  | "in_progress"
  | "completed"
  | "dispatched"
  | "cancelled";
  // New status field with integer values
  statusValue?: OrderStatusValue;
  paymentStatus: "pending" | "partial" | "paid" | "overdue" | "refunded";
  progressPercentage: number;
  products: Product[];

  // Comprehensive Statistics (new)
  productStatistics?: ProductStatistics;
  itemStatistics?: ItemStatistics;
  orderProgress?: OrderProgress;

  // Legacy fields for backward compatibility
  inProgressItems?: number;
  pendingItems?: number;

  totalProducts: number; // ← NEW: Total products count
  completedProducts: number; // ← NEW: Completed products count
  totalItems: number;
  completedItems: number;

  // Enhanced order details with items and stages
  orderDetails?: OrderDetails;

  created_by: string;
  updated_by: string;
  createdAt: Date | string;
  updatedAt: Date | string;
}

// Enhanced order details interface
export interface OrderDetails {
  totalItemsInOrder: number;
  items: OrderItemDetails[];
}

// Detailed item information with stages
export interface OrderItemDetails {
  id: string;
  itemNumber: string;
  number: string;
  type: string;
  width: string;
  height: string;
  status: string;
  currentStage: string;
  assignedTo: string | null;
  assignedToName: string;
  totalStages: number;
  completedStages: number;
  inProgressStages: number;
  pendingStages: number;
  progressPercentage: number;
  stages: ItemStageDetails[];
  stageHistory: ItemStageHistory[];
  createdAt: string;
  updatedAt: string;
}

// Detailed stage information
export interface ItemStageDetails {
  id: string;
  stage: string;
  status: 'pending' | 'in_progress' | 'completed';
  order: number;
  startedAt?: string;
  completedAt?: string;
  assignedTo?: string;
  assignedToName?: string;
  estimatedHours?: number;
  actualHours?: number;
  notes?: string;
  requiresQrScan?: boolean;
  requiresPhoto?: boolean;
  requiresSupervisorApproval?: boolean;
}

// Stage history for tracking changes
export interface ItemStageHistory {
  id: string;
  stage: string;
  previousStage?: string;
  status: string;
  userId: string;
  userName: string;
  timestamp: string;
  notes?: string;
  photoUrl?: string;
  location?: string;
  durationHours?: number;
  qualityCheck?: Record<string, any>;
  metadata?: Record<string, any>;
}

export interface Product {
  id: string;
  product_tracking_code: string; // PRD-DOO-001
  order_id: string;
  order_tracking_code: string; // ORD-2024-001
  customer_tracking_code: string; // CUST-ABC-001
  type: string; // Set type - dynamically fetched from product_masters table
  name?: string;
  productNumber?: string; // New format: 25080001/D001
  label?: string;
  location?: string;
  color?: string;
  notes?: string;
  evaOrder?: string;
  specifications?: any;
  total_items: number;
  completed_items: number;
  unit_price?: number;
  frame_count: number;
  panel_count: number;
  panel_window_count: number;
  net_count: number;
  quantity: number;
  metadata?: Record<string, any>;
  // New status field with integer values
  statusValue?: ProductStatusValue;
  // Status field for compatibility
  status?: "pending" | "in_progress" | "completed" | "dispatched";
  // Grouped item structure (new format) - using Item interface for full item data
  frame?: Item[];
  panel?: Item[];
  panelWindow?: Item[];
  net?: Item[];
  openableType?: Item[];
  // Legacy support
  items: Item[];
}

export interface Item {
  id: string;
  item_tracking_code: string; // ITM-PNL-ORD-2024-001-001
  order_tracking_code: string; // ORD-2024-001
  product_tracking_code: string; // PRD-DOO-001
  customer_tracking_code: string; // CUST-ABC-001
  qrCode?: string;
  qrCodeImageUrl?: string;
  productId: string;
  itemNumber?: string; // New format: 25080001/D001/F01
  type: ManufacturingItemType;
  currentStage?: string;
  currentStageOrder?: number;
  status: ItemStatus;
  // New status field with integer values
  statusValue?: ItemStatusValue;
  // Stages for this item
  stages?: Stage[];
  specifications?: {
    dimensions?: string;
    material?: string;
    color?: string;
    quantity?: number;
  };
  logs?: ItemLog[];
  assignedTo?: string;
  estimatedCompletion?: Date;
  completedAt?: Date;
  notes?: string;
  createdAt: Date;
  updatedAt: Date;
  product?: Product;
  quantity: number;
  deleted: boolean;
  deleted_at?: Date;
  deleted_by?: string;
}

// Tracking code validation and parsing utilities
export const TRACKING_CODE_PATTERNS = {
  customer: /^CUST-[A-Z]{3}-\d{3}$/,
  order: /^ORD-\d{4}-\d{3}$/,
  product: /^PRD-[A-Z]{3}-\d{3}$/,
  item: /^ITM-[A-Z]{3}-[A-Z0-9-]+-\d{3}$/,
  site: /^SITE-[A-Z]{4}-\d{3}$/,
} as const;

export type TrackingCodeType = keyof typeof TRACKING_CODE_PATTERNS;

export interface TrackingCodeValidation {
  valid: boolean;
  error?: string;
}

export interface ParsedTrackingCode {
  type: string;
  code?: string;
  sequence?: number;
  year?: number;
  productType?: string;
  itemType?: string;
  orderInfo?: string;
  siteCode?: string;
  error?: string;
}

// Tracking service interfaces
export interface TrackingSearchParams {
  customerCode?: string;
  orderCode?: string;
  productCode?: string;
  itemCode?: string;
  itemType?: string;
  status?: string;
  limit?: number;
}

export interface TrackingSummary {
  total_customers: number;
  total_orders: number;
  total_products: number;
  total_items: number;
  completed_items: number;
  in_progress_items: number;
  pending_items: number;
}

export interface TrackingDashboardData {
  summary: TrackingSummary;
  recentItems: ItemTrackingData[];
  trackingSystem: {
    version: string;
    features: string[];
  };
}
